import { Alert, notification, Skeleton } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { YearlyBudget } from '../../domain/budget'
import { LegacyJobData } from '../../domain/jobs/jobs'
import { useLegacyJobDataQuery, useUpdateJobMutation } from '../../domain/jobs/jobsQueries'
import { WorkareaGroupWithWorkareas } from '../../domain/workareas/group'
import { useBudgets } from '../../components/Budgets/budgetQueries'
import { CatalogBrowserContextWrapper } from '../../components/ItemCatalog/CatalogBrowserContext'
import ItemsRoot, { FocusedView } from '../../components/Items/ItemsRoot'
import JobDetailContent from './JobDetailContent'
import { applyWorkareaGroupChanges, focusedViewForTab, JobAnalysis, JobDetailTabKey, JobDetailTabKeys, parseJobId } from './jobDetailModel'
import { UpdateJobDetailsRequest } from '../../components/jobs/jobsApi'
import { applySavedJobDetails, buildJobSaveIntercomEvents, buildUpdateJobRequest, hasUnsavedJobChanges, mergeRefetchedJob, SaveJobOptions, validateJobForSave } from './jobSaveModel'

type Props = {
  /** The route param, which may be a "1234-slug" style job url. */
  jobId: number | string
}

const JobDetailPage = ({ jobId }: Props) => {
  const id = parseJobId(jobId)
  const jobQuery = useLegacyJobDataQuery(id)
  const budgetsQuery = useBudgets()

  const [job, setJob] = useState<LegacyJobData | undefined>()
  const [savedJob, setSavedJob] = useState<LegacyJobData | undefined>()
  const [activeTab, setActiveTab] = useState<JobDetailTabKey>(JobDetailTabKeys.Details)
  const [focusedView, setFocusedView] = useState<FocusedView>('workarea-estimated')
  const [triggerSummariesCounter, setTriggerSummariesCounter] = useState(0)
  const [saveCounter, setSaveCounter] = useState(0)
  const [hasPendingItemChanges, setHasPendingItemChanges] = useState(false)
  const [openReceiptId, setOpenReceiptId] = useState<number | undefined>()

  const analysisRef = useRef<JobAnalysis | undefined>()
  const hydratedJobIdRef = useRef<number | undefined>(undefined)
  const isDirtyRef = useRef(false)

  useEffect(() => {
    const data = jobQuery.data
    if (!data) {
      return
    }
    if (hydratedJobIdRef.current !== data.id) {
      // First load, or navigation to a different job: take the server state whole.
      hydratedJobIdRef.current = data.id
      setJob(data)
      setSavedJob(data)
      return
    }
    // A background refetch resolved while the page is open. If the user has
    // unsaved edits, keep them and merge only the server-owned display fields;
    // otherwise it is safe to take the fresh server state.
    if (isDirtyRef.current) {
      setJob(current => current ? mergeRefetchedJob(current, data) : data)
    } else {
      setJob(data)
      setSavedJob(data)
    }
  }, [jobQuery.data])

  const updateMutation = useUpdateJobMutation(updatedJob => {
    setJob(updatedJob)
    setSavedJob(updatedJob)
    setHasPendingItemChanges(false)
    setSaveCounter(x => x + 1)
  })

  const workareas = useMemo(() => job?.workareas ?? [], [job])

  const budgets = useMemo(
    () => (budgetsQuery.data?.budgets ?? [])
      .filter(x => x.year === job?.budgetYear)
      .map(x => x as YearlyBudget),
    [budgetsQuery.data, job?.budgetYear]
  )

  const handleTabChange = (tab: JobDetailTabKey) => {
    setActiveTab(tab)
    const view = focusedViewForTab(tab)
    if (view) {
      setFocusedView(view)
    }
  }

  const updateJob = (changes: Partial<LegacyJobData>) => {
    setJob(current => current ? { ...current, ...changes } : current)
  }

  const applyDetailsSave = (saved: UpdateJobDetailsRequest) => {
    setJob(current => current ? applySavedJobDetails(current, saved) : current)
    setSavedJob(current => current ? applySavedJobDetails(current, saved) : current)
  }

  const handleWorkareaChanges = (workareaGroups: WorkareaGroupWithWorkareas[]) => {
    setJob(current => current
      ? { ...current, workareas: applyWorkareaGroupChanges(current.workareas, workareaGroups) }
      : current)
    setTriggerSummariesCounter(x => x + 1)
  }

  const saveJob = (options?: SaveJobOptions, onSaved?: () => void) => {
    if (!job || updateMutation.isLoading) {
      return
    }

    const validation = validateJobForSave(job)
    if (!validation.isValid) {
      notification.info({ message: validation.message })
      return
    }

    const analysis = analysisRef.current
    if (analysis) {
      buildJobSaveIntercomEvents(job, savedJob, analysis, new Date())
        .forEach(event => window.Intercom?.('trackEvent', event.name, event.metadata))
    }

    updateMutation.mutate(buildUpdateJobRequest(job, options), { onSuccess: () => onSaved?.() })
  }

  if (jobQuery.isError) {
    const loadError = jobQuery.error as any
    return <div id="main" className="main-project fluid-container">
      <section>
        <Alert
          type="error"
          message="Error"
          description={loadError?.response?.data?.message ?? 'There was an error loading this job.'}
        />
      </section>
    </div>
  }

  if (!job) {
    return <div id="main" className="main-project fluid-container">
      <section><Skeleton active /></section>
    </div>
  }

  const isDirty = hasUnsavedJobChanges(job, savedJob) || hasPendingItemChanges
  // Kept in a ref so the hydration effect (which only depends on the query data)
  // can tell whether a refetch would be clobbering unsaved edits.
  isDirtyRef.current = isDirty

  return <CatalogBrowserContextWrapper
    allowDragging={focusedView === 'workarea-estimated' || focusedView === 'workarea-actual'}
    showTemplates={true}
  >
    <ItemsRoot
      jobId={job.id}
      jobType={job.type}
      jobIsSold={job.sold}
      workareas={workareas}
      budgets={budgets}
      focusedView={openReceiptId === undefined ? focusedView : 'receipt'}
      focusedReceiptId={openReceiptId}
      saveCounter={saveCounter}
      triggerSummariesCounter={triggerSummariesCounter}
      onTemplateAdd={() => { }}
      onChange={() => { }}
      autoSave={true}
    >
      <JobDetailContent
        job={job}
        activeTab={activeTab}
        isDirty={isDirty}
        isSaving={updateMutation.isLoading}
        onTabChange={handleTabChange}
        onJobChange={updateJob}
        onWorkareaChanges={handleWorkareaChanges}
        onReloadJob={() => jobQuery.refetch()}
        onDetailsSaved={applyDetailsSave}
        onSave={saveJob}
        onItemsChanged={() => setHasPendingItemChanges(true)}
        onAnalysisChange={analysis => { analysisRef.current = analysis }}
        openReceiptId={openReceiptId}
        onOpenReceipt={setOpenReceiptId}
      />
    </ItemsRoot>
  </CatalogBrowserContextWrapper>
}

export default JobDetailPage
